import { useLayoutEffect, useEffect, useState } from "react";
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { bookingsAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row } from "react-bootstrap";
import Swal from 'sweetalert2';
import { useSearchFilter } from '../useSearchFilter';

const Booking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(data, searchTerm, ['booking_id']);
  const [counts, setCounts] = useState({
    all: 0,
    regular: 0,
    fast: 0
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  // Fetch special bookings based on tab
  const fetchBookings = async (page = 1, search = '', tab = activeTab) => {
    try {
      setLoading(true);
      setError('');

      const params = { page, per_page: pagination.per_page, search };
      let response;

      switch (tab) {
        case "regular":
          response = await bookingsAPI.regular(params);
          break;
        case "fast":
          response = await bookingsAPI.fastOrders(params);
          break;
        case "completed":
          response = await bookingsAPI.completed(params);
          break;
        default:
          response = await bookingsAPI.index(params);
      }

      if (response.success) {
        setData(response.data.data || response.data);

        if (response.data.counts) {
          setCounts({
            all: response.data.counts.all || 0,
            regular: response.data.counts.regular || 0,
            fast: response.data.counts.fast || 0,
            completed: response.data.counts.completed || 0,
          });
        }

        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data?.length || 0,
        });

        // حفظ الـ tab الحالي في الـ URL state
        navigate(location.pathname, { 
          state: { 
            activeTab: tab,
            currentPage: page 
          } 
        });
      } else {
        setError(response.message || t('failed_to_fetch_bookings'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_bookings'));
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      const response = await bookingsAPI.index({ per_page: 1 });
      if (response.success && response.data.counts) {
        setCounts(response.data.counts);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      setUpdating(true);
      const response = await bookingsAPI.markPaid(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.marked_paid_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookings();
        fetchAllCounts();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_mark_paid"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchBookings(initialPage, searchTerm, initialTab);
    fetchAllCounts();
    
    // تنظيف الـ state بعد الاستخدام
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  // استخدام useEffect واحد فقط لمراقبة activeTab
  useEffect(() => {
    if (activeTab) {
      // عند تغيير الـ tab، إعادة تحميل البيانات للصفحة الأولى
      fetchBookings(1, '', activeTab);
    }
  }, [activeTab]);

  // إضافة useEffect للتمرير إلى الـ booking المميز
  useEffect(() => {
    if (highlightedBookingId && location.state?.scrollToHighlight) {
      const element = document.getElementById(`booking-${highlightedBookingId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
    }
  }, [data, highlightedBookingId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    // لا نستدعي fetchBookings هنا، سيتم استدعاؤها تلقائياً بواسطة useEffect
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchBookings(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_booking'));
      }
    } catch (err) {
      setError(t('failed_to_delete_booking'));
      console.error('Error deleting booking:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/bookings/details/${id}`, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (booking) => {
    if (!booking) return 0;
    
    const statusOrder = ['pending', 'approved', 'assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(booking.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  const getStatusBadge = (status) => {
      const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        approved: { variant: 'success', text: t('approved') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') }
      };
      
      const config = statusConfig[status] || { variant: 'light', text: status };
      return <Badge bg={config.variant}>{config.text}</Badge>;
    };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
    fetchBookings(1, '');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchBookings(1, searchTerm);
    }
  };

  return (
    <>
      <div className="driver-page">
        <div className="top-bar">
          <div className="d-flex gap-2">
            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={12}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('Search by booking ID...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </div>
          
          <div className="tabs">
            <button
              className={activeTab === "active" ? "active" : ""}
              onClick={() => handleTabChange("active")}
              disabled={loading}
            >
              <span className="count-badge">{counts.all}</span>
              {t("all_bookings")}
            </button>
            <button
              className={activeTab === "regular" ? "active" : ""}
              onClick={() => handleTabChange("regular")}
              disabled={loading}
            >
              <span className="count-badge">{counts.regular}</span>
              {t("regular_bookings")}
            </button>
            <button
              className={activeTab === "fast" ? "active" : ""}
              onClick={() => handleTabChange("fast")}
              disabled={loading}
            >
              <span className="count-badge">{counts.fast}</span>
              {t("fast_bookings")}
            </button>
            <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
              disabled={loading}
            >
              <span className="count-badge">{counts.completed}</span>
              {t("completed_bookings")}
            </button>
          </div>
          
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="orders">
          {filteredData.length === 0 ? (
            <div className="text-center py-5">
              <h4>{t('no_bookings_found')}</h4>
            </div>
          ) : (
            filteredData.map((booking) => (
              <div 
                className={`order-card ${booking.id === highlightedBookingId ? "booking-highlight" : ""}`} 
                key={booking.id}
                id={`booking-${booking.id}`}
              >
                <div className="order-header">
                  <span className="order-id">
                    <strong>
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: booking.booking_id?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        booking.booking_id
                      )}
                    </strong>
                  </span>

                  <span className="driver">
                    <strong>{t("user")}:</strong> 
                    {booking.user.id !== null ? (
                      <Button
                        variant="link"
                        className="p-0 ms-1 text-decoration-none"
                        onClick={() => navigate(`/admin/customers/details/${booking.user?.id}`)}
                      >
                        {booking.user?.user_name || t('not_assigned')}
                      </Button>
                    ) : (
                      <span className='ms-1'>
                        {booking.user?.user_name || t('not_assigned')}
                      </span>
                    )}
                  </span>

                  <span className="vehicle">
                    <FaTruck />
                    &nbsp;
                    <small>{booking.vehicle_name}</small>
                  </span>

                  <span className="created">{booking.created_at}</span>

                  <span className="price">
                    {getStatusBadge(booking.status || 'pending')}

                    <Badge bg={booking.payment_status ? 'success' : 'warning'} className="ms-2">
                      {t(booking.payment) || t('unpaid')}
                    </Badge>

                    {booking.fast_order == 1 && (
                      <Badge bg={booking.fast_order ? 'success' : 'warning'} className="ms-2">
                        {t('booking.fast_booking')}
                      </Badge>
                    )}
                  </span>
                </div>

                <div className="order-body">
                  <div className="left d-flex flex-wrap-wrap justify-content-between flex-wrap">
                    <div className="d-flex flex-column">
                      <strong>{t("booking.loading_company")}:</strong> 
                      {booking.loading_company?.[0] || t('not_available')}
                    </div>
                    <div className="d-flex flex-column">
                      <strong>{t("booking.unloading_company")}:</strong> 
                      {booking.unloading_company?.[0] || t('not_available')}
                    </div>

                    <div className="delivery-path-vertical">
                      <div className="circle-label-vertical">
                        <span className="circle"></span>
                        <span className="label">{booking.booking_from?.[0]}</span>
                      </div>
                      <div className="line-container">
                        <div className="line-vertical"></div>
                      </div>
                      <div className="circle-label-vertical">
                        <span className="circle"></span>
                        <span className="label">{booking.booking_to?.[0]}</span>
                      </div>
                    </div>
                  </div>

                  <div className="right d-flex flex-wrap-wrap justify-content-between">
                    <div className="progress-bar-container mt-2">
                      <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                        <strong>{t("progress")}:</strong>
                        <span className="progress-label">{getProgressPercentage(booking)}%</span>
                      </div>
                      
                      <div className="progress-bar-outer">
                        <div
                          className="progress-bar-inner"
                          style={{
                            width: `${getProgressPercentage(booking)}%`,
                            background: getProgressColor(getProgressPercentage(booking)),
                          }}
                        ></div>
                      </div>
                    </div>
                    <span className="price">
                      {booking.total} EUR
                    </span>
                    <span className="price"></span>
                    <span className="price"> </span>
                  </div>
                </div>

                <div className="order-footer">
                  <div>
                    <strong>
                      {t("booking.loading_date_time")}:
                    </strong>{" "}
                    {booking.loading_date_time || booking.created_at}
                  </div>
                  <div>
                    <strong>{t("distance")}:</strong> {booking.distance} | 
                    <strong> {t("duration")}:</strong> {booking.total_duration}
                  </div>
                  <div>
                    <strong>{t("booking.total_weight")}:</strong> {booking.total_weight} Kg
                  </div>

                  <div className="action-buttons">
                    {booking.payment_status === 0 && (
                      <Button 
                        size="sm"
                        variant={booking.payment_status ? "success" : "outline-success"}
                        onClick={() => handleMarkPaid(booking.id)}
                        disabled={updating || booking.payment_status}
                      >
                        <FaEuroSign /> {t("booking.mark_paid")}
                      </Button>
                    )}
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewDetails(booking.id)}
                      className="mx-2"
                    >
                      <FaInfoCircle /> {t("details")}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteClick(booking.id)}
                    >
                      <FaTrashAlt /> {t("delete")}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchBookings}
            disabled={loading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('confirm_delete_booking')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .booking-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
        }
        
        .booking-highlight .order-header,
        .booking-highlight .order-body,
        .booking-highlight .order-footer {
          background-color: transparent;
        }
        
        .booking-highlight .order-id strong {
          color: #155724;
          font-size: 1.1em;
        }
        
        .tabs button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .tabs button.active:disabled {
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default Booking;